import { apiEvents } from "@/api/event.api.ts";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EventPreviewModal } from "@/components/event/EventPreviewModal.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { TodayItem } from "@/pages/home/home.types.ts";
import { useEmployeeMe, useEmployees } from "@/queries/employees/use-employees.query.ts";
import { useEvents } from "@/queries/use-events.query.ts";
import { ApiSortingDirection } from "@/types/api.types.ts";
import { EmployeeColumnId } from "@/types/column.types.ts";
import {
  ApiEmployee,
  ApiEmployeeFilter,
  getActiveEmployeeLifecycleStatuses,
} from "@/types/employees/employee.types.ts";
import { ApiEvent, getAttendeeResponseText } from "@/types/event.types.ts";
import { getJobEmoji } from "@/types/recruit/job.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  Box,
  Button,
  FontAwesomeIcon,
  ListItem,
  Skeleton,
  Stack,
  TabItem,
  Tabs,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { add, isSameDay, isToday, startOfDay } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface CelebrationItem {
  type: "birthday" | "anniversary";
  date: Date;
  employee: ApiEmployee;
  yearsCount?: number;
}

export const HomeUpcomingEvents = () => {
  const { t } = useTranslation();
  const { data: employee } = useEmployeeMe();
  const { openModal } = useModals();
  const { renderShortDate, renderEmployeeName, renderRelativeDate, renderDateAndTime } =
    useFormatter();
  const { isFeatureEnabled } = usePermissions();

  const { data: upcomingEvents, isLoading: upcomingEventsIsLoading } = useEvents({
    date_start: startOfDay(new Date()),
    date_end: add(startOfDay(new Date()), { days: 5 }),
  });

  const upcomingBirthdaysFilter: ApiEmployeeFilter = {
    page_number: 1,
    sorting: {
      fields: [
        {
          field: EmployeeColumnId.birth_date_relative,
          direction: ApiSortingDirection.asc,
        },
      ],
    },
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
  };
  const { data: upcomingBirthdays, isLoading: upcomingBirthdaysIsLoading } =
    useEmployees(upcomingBirthdaysFilter);

  const upcomingAnniversariesFilter: ApiEmployeeFilter = {
    page_number: 1,
    sorting: {
      fields: [
        {
          field: EmployeeColumnId.hired_on_relative,
          direction: ApiSortingDirection.asc,
        },
      ],
    },
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
  };

  const { data: upcomingAnniversaries, isLoading: upcomingAnniversariesIsLoading } = useEmployees(
    upcomingAnniversariesFilter
  );

  const combinedCelebrations = useMemo(() => {
    const celebrations: CelebrationItem[] = [];
    const today = startOfDay(new Date());
    const oneMonthFromNow = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());

    upcomingBirthdays?.items?.forEach((employee) => {
      if (employee.birth_date?.month && employee.birth_date?.day) {
        const birthdayDate = new Date(
          today.getFullYear(),
          employee.birth_date.month - 1,
          employee.birth_date.day
        );

        if (birthdayDate < today) {
          birthdayDate.setFullYear(today.getFullYear() + 1);
        }

        if (birthdayDate >= today && birthdayDate < oneMonthFromNow) {
          celebrations.push({
            type: "birthday",
            date: birthdayDate,
            employee: employee,
          });
        }
      }
    });

    upcomingAnniversaries?.items?.forEach((employee) => {
      if (employee.date_hired_on) {
        const hireDate = new Date(employee.date_hired_on);
        const anniversaryDate = new Date(
          today.getFullYear(),
          hireDate.getMonth(),
          hireDate.getDate()
        );

        if (anniversaryDate < today) {
          anniversaryDate.setFullYear(today.getFullYear() + 1);
        }

        if (anniversaryDate >= startOfDay(today) && anniversaryDate <= oneMonthFromNow) {
          const yearsCount = anniversaryDate.getFullYear() - hireDate.getFullYear();

          if (yearsCount > 0) {
            celebrations.push({
              type: "anniversary",
              date: anniversaryDate,
              employee: employee,
              yearsCount: yearsCount,
            });
          }
        }
      }
    });

    return celebrations.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [upcomingBirthdays, upcomingAnniversaries]);

  const [showAll, setShowAll] = useState(false);
  const isLoading =
    upcomingEventsIsLoading || upcomingBirthdaysIsLoading || upcomingAnniversariesIsLoading;
  const buildTodayItemFromEvent = (event: ApiEvent) => {
    const add: TodayItem = {};
    add.id = event.id;
    add.preTitle = event.title;
    const texts: string[] = [];
    texts.push(
      isToday(event.datetime_start)
        ? renderRelativeDate(event.datetime_start)
        : renderDateAndTime(event.datetime_start)
    );
    for (const attendee of event.attendees) {
      if (attendee.employee) {
        if (attendee.employee.id != employee?.id) {
          texts.push(renderEmployeeName(attendee.employee));
        }
      } else if (attendee.email) {
        texts.push(attendee.email);
      }
    }
    if (event.interview && event.interview.job) {
      texts.push(event.interview.job.title);
      add.emoji = getJobEmoji(event.interview.job);
    }

    add.title = texts.length > 4 ? texts.slice(0, 4).join(" · ") + "..." : texts.join(" · ");
    add.buttonTitle = t("common:view");

    const meAsAttendee = event.attendees.find((x) => x.employee?.id == employee?.id);
    if (meAsAttendee) {
      add.caption = getAttendeeResponseText(meAsAttendee.response_status);
    }

    add.onClick = async () => {
      if (event) {
        const { data } = await apiEvents.getEventById(event.id);
        if (data) {
          openModal(EventPreviewModal, {
            event: data,
          });
        }
      }
    };

    return add;
  };

  const filteredEvents = useMemo(() => {
    if (!upcomingEvents) return [];
    const now = new Date();

    return upcomingEvents.filter((event) => {
      const eventEndDate = event.datetime_end || event.datetime_start;
      return eventEndDate >= now;
    });
  }, [upcomingEvents]);

  const hasUpcomingEvents = Boolean(filteredEvents.length > 0);
  const hasUpcomingCelebrations = Boolean(
    isFeatureEnabled(ApiFeatureType.core_company_calendar) && combinedCelebrations.length > 0
  );
  const isAnyUpcoming = hasUpcomingEvents || hasUpcomingCelebrations;

  const todayEvents = useMemo(() => {
    return (
      filteredEvents
        .filter((x) => x.datetime_start <= add(startOfDay(new Date()), { days: 1 }))
        .map(buildTodayItemFromEvent) || []
    );
  }, [filteredEvents]);

  const tomorrowEvents = useMemo(() => {
    return (
      filteredEvents
        .filter(
          (x) =>
            x.datetime_start > add(startOfDay(new Date()), { days: 1 }) &&
            x.datetime_start <= add(startOfDay(new Date()), { days: 2 })
        )
        .map(buildTodayItemFromEvent) || []
    );
  }, [filteredEvents]);

  const laterEvents = useMemo(() => {
    return (
      filteredEvents
        .filter((x) => x.datetime_start > add(startOfDay(new Date()), { days: 2 }))
        .map(buildTodayItemFromEvent) || []
    );
  }, [filteredEvents]);

  const [isLoadingHomeItemId, setIsLoadingHomeItemId] = useState<number | string | undefined>(
    undefined
  );

  const isCelebrationToday = (date: Date): boolean => {
    const today = startOfDay(new Date());
    return isSameDay(today, date);
  };

  return (
    <>
      {isLoading && <Skeleton className={"w-full h-5"}></Skeleton>}
      {isAnyUpcoming && (
        <Box>
          <Title
            header={t("calendar:upcoming")}
            size={"lg"}
            paddingBottom
            paddingTop
            actionIcon={<FontAwesomeIcon icon={"fa-solid fa-chevron-right"} />}
            to={"/calendar"}
          />

          <Box className={"bg-light border border-dark/5 rounded-xs"}>
            <Tabs>
              {hasUpcomingEvents && (
                <TabItem title={t("calendar:events")}>
                  <Stack className={"px-8 pb-8"}>
                    {todayEvents.length > 0 && (
                      <>
                        <Title header={t("common:today")} size={"xs"} paddingBottom paddingTop />
                        {todayEvents.map((item, i) => (
                          <ListItem
                            key={`event-${i}`}
                            emoji={item.emoji}
                            avatarUrl={item.avatarUrl}
                            caption={item.caption}
                            title={item.title}
                            preTitle={item.preTitle}
                            emphasis={"top"}
                            valueSlot={
                              <Button
                                isLoading={isLoadingHomeItemId == item.id}
                                variant={"plain"}
                                onClick={async () => {
                                  setIsLoadingHomeItemId(item.id);
                                  await item.onClick?.();
                                  setIsLoadingHomeItemId(undefined);
                                }}
                              >
                                {item.buttonTitle}
                              </Button>
                            }
                          />
                        ))}
                      </>
                    )}
                    {tomorrowEvents.length > 0 && (
                      <>
                        <Title header={t("common:tomorrow")} size={"xs"} paddingBottom paddingTop />
                        {tomorrowEvents.map((item, i) => (
                          <ListItem
                            key={`event-${i}`}
                            emoji={item.emoji}
                            avatarUrl={item.avatarUrl}
                            caption={item.caption}
                            title={item.title}
                            preTitle={item.preTitle}
                            emphasis={"top"}
                            valueSlot={
                              <Button
                                isLoading={isLoadingHomeItemId == i}
                                variant={"plain"}
                                onClick={async () => {
                                  setIsLoadingHomeItemId(item.id);
                                  await item.onClick?.();
                                  setIsLoadingHomeItemId(undefined);
                                }}
                              >
                                {item.buttonTitle}
                              </Button>
                            }
                          />
                        ))}
                      </>
                    )}
                    {laterEvents.length > 0 && (
                      <>
                        <Title header={t("common:later")} size={"xs"} paddingBottom paddingTop />
                        {laterEvents.slice(0, showAll ? laterEvents.length : 5).map((item, i) => (
                          <ListItem
                            key={`event-${i}`}
                            emoji={item.emoji}
                            avatarUrl={item.avatarUrl}
                            caption={item.caption}
                            title={item.title}
                            preTitle={item.preTitle}
                            emphasis={"top"}
                            valueSlot={
                              <Button
                                isLoading={isLoadingHomeItemId == i}
                                variant={"plain"}
                                onClick={async () => {
                                  setIsLoadingHomeItemId(item.id);
                                  await item.onClick?.();
                                  setIsLoadingHomeItemId(undefined);
                                }}
                              >
                                {item.buttonTitle}
                              </Button>
                            }
                          />
                        ))}
                        {!showAll && laterEvents.length > 5 && (
                          <Box className={"pt-8"}>
                            <Button
                              onClick={() => setShowAll(!showAll)}
                              size={"sm"}
                              variant={"secondary"}
                            >
                              {t("common:show_more") + "..."}
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </Stack>
                </TabItem>
              )}
              {hasUpcomingCelebrations && (
                <TabItem title={t("calendar:celebrations")}>
                  <Stack className={"p-8"}>
                    {combinedCelebrations
                      .slice(0, showAll ? combinedCelebrations.length : 5)
                      .map((celebration, i) => {
                        const formattedDate = renderShortDate(celebration.date);
                        const isToday = isCelebrationToday(celebration.date);

                        return (
                          <ListItem
                            key={`celebration-${i}`}
                            icon={
                              <EmployeeAvatar
                                employee={celebration.employee}
                                size={"md"}
                                subAvatar={{
                                  emoji: celebration.type === "birthday" ? ":birthday:" : ":tada:",
                                }}
                              />
                            }
                            title={`${formattedDate} - ${
                              celebration.type === "birthday"
                                ? celebration.employee.id === employee?.id
                                  ? isToday
                                    ? t("calendar:happy_birthday")
                                    : t("calendar:get_ready_to_celebrate_your_birthday")
                                  : isToday
                                    ? t("calendar:congratulate_colleague")
                                    : t("calendar:get_ready_to_celebrate_colleague_birthday")
                                : t("plurals:work_anniversary_with_ordinal", {
                                    years: celebration.yearsCount,
                                    ordinal: true,
                                  })
                            }`}
                            preTitle={renderEmployeeName(celebration.employee)}
                            emphasis={"top"}
                            valueSlot={
                              <Button
                                isLoading={isLoadingHomeItemId == i}
                                variant={"plain"}
                                onClick={() => {
                                  openModal(EmployeeViewModal, {
                                    employeeId: celebration.employee.id,
                                  });
                                }}
                              >
                                {t("core:view_profile")}
                              </Button>
                            }
                          />
                        );
                      })}
                    {!showAll && combinedCelebrations.length > 5 && (
                      <Box className={"pt-8"}>
                        <Button
                          onClick={() => setShowAll(!showAll)}
                          size={"sm"}
                          variant={"secondary"}
                        >
                          {t("common:show_more") + "..."}
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </TabItem>
              )}
            </Tabs>
          </Box>
        </Box>
      )}
    </>
  );
};
